import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import stringToColor from 'string-to-color';
// mui components
import { LoadingButton, TabContext, TabList, TabPanel } from '@mui/lab';
import { Dialog, Grid, Tab, Typography, FormControl, Select, MenuItem } from '@mui/material';
import { Box, styled } from '@mui/system';
// components
import ActionButton from 'components/buttons';
import { LockedItem } from 'components/locked-item';
import StyledForm from 'components/styled-form';
import SelectController from 'controllers/select-controller';
import CompleteItemModal from '../complete-item-modal';
import DeleteItemModal from '../delete-item-modal';
import IncompleteItemModal from '../incomplete-item-modal';
// sections@h
import LinkedSection from './sections/linked-section';
import ListSection from './sections/list-section';
import { useWritableProjects } from 'hooks/useWritableProjects';
import { IProject } from '_interfaces/project';
// api
import { useGetCompanyRolePermissionsMeQuery } from 'redux-store/api/company-roles';
import {
  useCreateSchedulePhaseItemMutation,
  useUpdateSchedulePhaseItemMutation,
} from 'redux-store/api/schedule-phases-items';
import { useAppDispatch } from 'redux-store/hooks/useAppDispatch';
import {
  handleAllowGanttRefetch,
  handleChangedFromDatePicker,
  handleShowRefetchLoader,
} from 'redux-store/slices/manage-gantt-slice';
// types
import { PhaseStatusesEnum } from 'redux-store/types';
import { ILinkedItem, IPhaseItemFormValues, IPhaseItemModalProps, IPredecessor } from './types';
// utils
import useToast from 'hooks/useToast';
import { useValidationNotification } from 'hooks/useValidationNotification';
import { TEMP_ITEM_ID_COEFFICIENT } from 'utils/constants';
import { weekDays } from 'utils/helpers';
import { calculateBusinessDays, utcDateToZoned, zonedDateToUtc } from 'utils/postDateHelper';
import { PhaseItemEditModalSchema, PhaseItemModalSchema } from './validation';
// icons
import CheckmarkIcon from 'assets/icons/buttons/checkmark.svg';
// styles
import { useSyncSelectedProject } from 'hooks/useSyncSelectedProject';
import { ScheduleActivityFeed } from 'pages/project-dashboard/sections/schedule/modals/phase-item-modal/components/schedule-activity-feed/schedule-activity-feed';
import { formatToCalendarItem } from '../../sections/calendar/utils/helpers';
import { newPhaseItemModalStyles } from './styles';
import { getPhaseItemsStartEndDates } from 'redux-store/api/projects/project.utils';

const StyledTabList = styled(TabList)(newPhaseItemModalStyles.tabList);
const LinkTab = ({ ...props }) => <Tab {...props} sx={newPhaseItemModalStyles.tab} />;

const PhaseItemModal: React.FC<IPhaseItemModalProps> = ({
  days,
  open,
  gantt,
  phases,
  lagType,
  projectId,
  ganttItemId,
  currentPhase,
  currentPhaseItem,
  closeModal,
  updateGanttChart,
  handleDeleteTask,
  handleSetScrollingId,
  handleAddCalendarTask,
  handleAllowUpdateProject,
  isAllProjects,
  allProjects,
}) => {
  const [createPhaseItem, { isLoading: createLoading }] = useCreateSchedulePhaseItemMutation();
  const [updatePhaseItem, { isLoading: updateLoading }] = useUpdateSchedulePhaseItemMutation();

  const [tabValue, setTabValue] = useState('details');
  const [deletedPhaseItem, setDeletedPhaseItem] = useState(null);
  const [completedPhaseItem, setCompletedPhaseItem] = useState(null);
  const [incompletedPhaseItem, setIncompletedPhaseItem] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);

  const { data: userPermissions } = useGetCompanyRolePermissionsMeQuery();

  const showToast = useToast();
  const dispatch = useAppDispatch();

  const writableProjects = useWritableProjects(allProjects);
  const projectOptions = writableProjects.map((p) => ({
    label: p.name,
    value: p.id,
  }));

  const isEdit = useMemo(() => !!currentPhaseItem, [currentPhaseItem]);
  const activeProjectId = isAllProjects ? (isEdit ? projectId : selectedProjectId) : projectId;
  const activeProject = isAllProjects
    ? allProjects?.find((p) => p.id === (isEdit ? Number(projectId) : selectedProjectId))
    : null;
  const activePhases = isAllProjects ? activeProject?.phases || [] : phases;
  const activeLagType = isAllProjects ? activeProject?.lagType || lagType : lagType;

  const { project } = useSyncSelectedProject(isAllProjects ? activeProjectId : projectId);

  const projectWorkingDays = useRef(project?.workingDays || []);
  projectWorkingDays.current = project?.workingDays || [];

  const nonWorkingDays = weekDays.filter((x: any) => !projectWorkingDays.current.includes(x));
  const isItemCompleted = currentPhaseItem?.status === PhaseStatusesEnum.COMPLETED;

  const isActivityFeedVisible =
    userPermissions?.ENABLE_ACTIVITY_FEED?.canCreate &&
    userPermissions?.ENABLE_SCHEDULE_ACTIVITY_FEED?.canCreate &&
    isEdit;

  const isDisabled = (date) => {
    const day = dayjs(date).day();
    return nonWorkingDays.includes(day);
  };

  const handleChangeTab = useCallback((_event, newValue: string) => {
    setTabValue(newValue);
  }, []);

  const phasesOptions = useMemo(
    () =>
      activePhases
        .slice()
        .sort((a, b) => a.id - b.id)
        .map((phase) => ({
          label: phase.title,
          value: phase.id,
          color: phase.color || stringToColor(phase.title),
        })),
    [activePhases]
  );

  const itemsList = useMemo(() => {
    const list = activePhases.flatMap(({ items }) => items);

    if (currentPhaseItem) {
      return list.filter(({ id }) => id !== currentPhaseItem.id);
    }

    return list;
  }, [activePhases, currentPhaseItem]);

  const formDefaultValues = useMemo(() => {
    const defaultValues: {
      title: string;
      phaseId: number;
      startDate: string | Date | dayjs.Dayjs;
      endDate: string | Date | dayjs.Dayjs;
      predecessors: IPredecessor[];
      linkedItems: ILinkedItem[];
      workDays: number | null;
    } = {
      title: '',
      phaseId: currentPhase?.id || activePhases?.[0]?.id,
      startDate: utcDateToZoned(zonedDateToUtc(new Date())),
      endDate: utcDateToZoned(zonedDateToUtc(new Date())),
      predecessors: [] as IPredecessor[],
      linkedItems: [] as ILinkedItem[],
      workDays: 1,
    };

    if (isEdit) {
      return { ...defaultValues, reasonForChange: '' };
    }

    return defaultValues;
  }, [phasesOptions, isEdit]);

  const form = useForm({
    resolver: yupResolver(isEdit ? PhaseItemEditModalSchema : PhaseItemModalSchema),
    defaultValues: formDefaultValues,
  });

  useEffect(() => {
    if (!!currentPhaseItem) {
      const currentWorkDays = calculateBusinessDays(
        currentPhaseItem?.startDate,
        currentPhaseItem?.endDate,
        projectWorkingDays.current
      );

      const dateRangesById = getPhaseItemsStartEndDates(activePhases);

      form.reset({
        title: currentPhaseItem.title,
        phaseId: currentPhaseItem.phaseId,
        startDate: currentPhaseItem.startDate ? utcDateToZoned(currentPhaseItem.startDate) : '',
        endDate: currentPhaseItem.endDate ? utcDateToZoned(currentPhaseItem.endDate) : '',
        predecessors: currentPhaseItem?.predecessors?.map((pred) => {
          return {
            ...pred,
            lagDays: pred.lagDays ?? 0,
            workDaysDifference: pred.workDaysDifference ?? 0,
            startDate: dateRangesById?.[pred.predecessorId].startDate,
            endDate: dateRangesById?.[pred.predecessorId].endDate,
            workDays: dateRangesById?.[pred.predecessorId].workDays,
          };
        }),
        linkedItems: currentPhaseItem?.linkedItems?.map((item) => {
          const linkedItem = itemsList?.filter(({ id }) => id === item?.itemId)[0];

          const formatedLinkEndDate = dayjs(linkedItem?.endDate).utc().format('MMM D, YYYY');
          const formatedLinkStartDate = dayjs(linkedItem?.startDate).utc().format('MMM D, YYYY');

          return {
            ...item,
            isLinked: true,
            name: linkedItem.title,
            startDate: linkedItem.startDate ? formatedLinkStartDate : '-',
            endDate: linkedItem.endDate ? formatedLinkEndDate : '-',
          };
        }),

        workDays: currentWorkDays,
      });
    }

    // eslint-disable-next-line
  }, [currentPhaseItem, itemsList]);

  const endDate = useWatch({ control: form.control, name: 'endDate' });
  const startDate = useWatch({ control: form.control, name: 'startDate' });

  const handleCloseModal = useCallback(() => {
    closeModal();
    form.reset(formDefaultValues);
    setTabValue('details');
    dispatch(handleChangedFromDatePicker(false));
  }, [closeModal, form, formDefaultValues]);

  const handleClosePhaseItemDeleteModal = useCallback(() => {
    setDeletedPhaseItem(null);
  }, []);

  const handleCloseIncompletedModal = () => {
    setIncompletedPhaseItem(null);
  };

  const handleOpenPhaseItemDeleteModal = useCallback(() => {
    setDeletedPhaseItem(currentPhaseItem);
  }, [currentPhaseItem]);

  const handleCloseCompletionModal = useCallback(() => {
    setCompletedPhaseItem(null);
  }, []);

  const handleSetCompletedPhaseItem = useCallback(() => {
    if (!!isEdit && currentPhaseItem?.status !== PhaseStatusesEnum.COMPLETED) {
      setCompletedPhaseItem(currentPhaseItem);
    }

    if (!!isEdit && currentPhaseItem?.status === PhaseStatusesEnum.COMPLETED) {
      setIncompletedPhaseItem(currentPhaseItem);
    }
  }, [currentPhaseItem, isEdit]);

  const normalizeLag = (lagDays: number | undefined): number | undefined => {
    return Number.isFinite(lagDays) ? Math.max(0, lagDays) : undefined;
  };

  const onSubmit = async ({ linkedItems, ...formValues }: IPhaseItemFormValues) => {
    if (!activeProjectId) {
      showToast({
        message: 'Please select a project',
        type: 'error',
      });
      return;
    }

    const formData = {
      ...formValues,
      startDate: zonedDateToUtc(formValues.startDate),
      endDate: zonedDateToUtc(formValues.endDate),
    };

    if (!!currentPhaseItem) {
      const successors = linkedItems?.map((linkItem) => {
        const normalizedLag = normalizeLag(linkItem?.lagDays);

        return {
          itemId: linkItem?.itemId,
          lagDays: normalizedLag,
          predecessorId: linkItem?.predecessorId,
          taskDependency: linkItem?.taskDependency,
        };
      });

      const editItemPredecessors = formValues?.predecessors?.map(
        ({ id, itemId, ...predecessor }) => {
          const normalizedLag = normalizeLag(predecessor?.lagDays);

          return {
            taskDependency: predecessor?.taskDependency,
            lagType: predecessor?.lagType,
            predecessorId: predecessor?.predecessorId,
            itemId: currentPhaseItem?.id,
            lagDays: normalizedLag,
            minimumLagDays: normalizedLag,
          };
        }
      );

      updatePhaseItem({
        ...formData,
        withCascadeUpdates: true,
        id: currentPhaseItem.id,
        linkedItems: successors,
        predecessors: editItemPredecessors,
        projectId: activeProjectId,
      })
        .unwrap()
        .then((res) => {
          showToast({ type: 'success', message: 'Item has been  updated.' });

          if (gantt && !!updateGanttChart) {
            updateGanttChart();
          }

          if (!!handleAllowUpdateProject && isAllProjects) {
            handleAllowUpdateProject();
          }

          handleCloseModal();
        })
        .catch((err) => {
          showToast({
            type: 'error',
            message:
              typeof err?.data?.message === 'string'
                ? err?.data?.message
                : 'Something went wrong. Please try again or contact support for help.',
          });
        });
    } else {
      const createItemPredecessors = formValues?.predecessors?.map(
        ({ id, itemId, ...predecessor }) => {
          const normalizedLag = normalizeLag(predecessor?.lagDays);

          return {
            taskDependency: predecessor?.taskDependency,
            lagDays: normalizedLag,
            lagType: predecessor?.lagType,
            predecessorId: predecessor?.predecessorId,
            itemId: currentPhaseItem?.id,
            minimumLagDays: normalizedLag,
          };
        }
      );

      createPhaseItem({
        ...formData,
        projectId: activeProjectId,
        predecessors: createItemPredecessors,
      })
        .unwrap()
        .then((res) => {
          showToast({ type: 'success', message: 'Item has been added to the schedule.' });
          if (gantt) {
            dispatch(handleAllowGanttRefetch(true));
            dispatch(handleShowRefetchLoader(true));
            handleSetScrollingId(res.id * TEMP_ITEM_ID_COEFFICIENT);
          }

          if (!!handleAddCalendarTask && !isAllProjects) {
            const itemParent = activePhases.find(({ id }) => id === res.phaseId);
            const formatedItem = formatToCalendarItem(res, itemParent);
            handleAddCalendarTask(formatedItem);
          }

          if (!!handleAllowUpdateProject && isAllProjects) {
            handleAllowUpdateProject();
          }

          handleCloseModal();
        })
        .catch((err) => {
          showToast({
            type: 'error',
            message:
              typeof err?.data?.message === 'string'
                ? err?.data?.message
                : 'Something went wrong. Please try again or contact support for help.',
          });
        });
    }
  };

  useValidationNotification(form.formState.isSubmitSuccessful, form.formState.submitCount);

  const DEFAULT_WORK_DAYS = 1;

  const currentWorkDays = currentPhaseItem
    ? calculateBusinessDays(
        currentPhaseItem?.startDate,
        currentPhaseItem?.endDate,
        project?.workingDays
      )
    : DEFAULT_WORK_DAYS;

  return (
    <Dialog
      open={open}
      sx={newPhaseItemModalStyles.modal(isActivityFeedVisible)}
      onClose={handleCloseModal}
    >
      <Grid sx={{ display: 'flex' }}>
        <FormProvider {...form}>
          <StyledForm onSubmit={form.handleSubmit(onSubmit)} sx={newPhaseItemModalStyles.form}>
            <Grid sx={newPhaseItemModalStyles.header}>
              <Grid sx={newPhaseItemModalStyles.titleContainer}>
                <Typography sx={newPhaseItemModalStyles.title}>
                  {isEdit ? 'Edit Schedule Item' : 'Add Schedule Item'}
                </Typography>

                <Typography sx={newPhaseItemModalStyles.subtitle}>
                  Select phase and add name for the item.
                </Typography>
              </Grid>

              {isItemCompleted ? (
                <LockedItem />
              ) : isEdit ? (
                <ActionButton
                  variant="secondary"
                  label="Mark as complete"
                  type="button"
                  startIcon={<CheckmarkIcon />}
                  onClick={handleSetCompletedPhaseItem}
                  loading={updateLoading}
                />
              ) : null}
            </Grid>

            <TabContext value={tabValue}>
              <Grid
                sx={
                  isEdit
                    ? newPhaseItemModalStyles.tabsContainer
                    : newPhaseItemModalStyles.tabsContainerEdit
                }
              >
                <StyledTabList aria-label="phase item tabs" onChange={handleChangeTab}>
                  {isEdit && (
                    <LinkTab
                      label="Item Details & Predecessors"
                      value="details"
                      sx={newPhaseItemModalStyles.tab}
                    />
                  )}

                  {isEdit && (
                    <LinkTab
                      label={`Successors (${form?.getValues('linkedItems')?.length || 0})`}
                      value="linked"
                      sx={newPhaseItemModalStyles.tab}
                    />
                  )}
                </StyledTabList>
              </Grid>
              <Grid sx={newPhaseItemModalStyles.tabWrapper}>
                <TabPanel value="details" sx={newPhaseItemModalStyles.tabPanel}>
                  <Box>
                    {isAllProjects && !isEdit && (
                      <Grid sx={{ width: '100%', padding: '0 0 16px 0' }}>
                        <FormControl fullWidth>
                          <Typography
                            component="label"
                            htmlFor="project-select"
                            sx={{
                              width: '100%',
                              marginBottom: '8px',
                              fontSize: '14px',
                              lineHeight: '146%',
                              fontWeight: 400,
                              color: '#23252F',
                              fontFamily: 'Inter, sans-serif',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            Project
                          </Typography>
                          <Select
                            id="project-select"
                            value={selectedProjectId || ''}
                            onChange={(e) => setSelectedProjectId(e.target.value as number)}
                            disabled={projectOptions.length === 0}
                            displayEmpty
                            renderValue={(selected) => {
                              if (!selected) {
                                return <span style={{ color: '#8C8F9D' }}>Select Project</span>;
                              }
                              return projectOptions.find((opt) => opt.value === selected)?.label;
                            }}
                            sx={{
                              height: '48px',
                              borderRadius: '12px',
                              backgroundColor: '#fff',
                              '& .MuiOutlinedInput-notchedOutline': {
                                border: '1px solid #EDEFF7',
                                borderRadius: '12px',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                border: '1px solid #EDEFF7',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#EDEFF7',
                                borderWidth: '1px',
                              },
                              '& .MuiSelect-select': {
                                fontSize: '14px',
                                lineHeight: '146%',
                                fontWeight: 400,
                                color: '#23252F',
                                fontFamily: 'Inter, sans-serif',
                              },
                            }}
                          >
                            {projectOptions.length === 0 ? (
                              <MenuItem value="" disabled>
                                No projects available
                              </MenuItem>
                            ) : (
                              projectOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))
                            )}
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                    <ListSection
                      phasesOptions={phasesOptions}
                      currentPhase={currentPhase}
                      endDate={endDate}
                      startDate={startDate}
                      shouldDisableDate={isDisabled}
                      currentStartDate={currentPhaseItem?.startDate}
                      days={days}
                      currentWorkDays={currentWorkDays}
                      workingDays={project?.workingDays}
                      itemsList={itemsList}
                      gantt={gantt}
                      isEdit={Boolean(currentPhaseItem)}
                      containerAdditionalStyles={{
                        padding: '0 0 20px 0',
                      }}
                      isItemCompleted={isItemCompleted}
                      lagType={activeLagType}
                      project={project}
                    />
                  </Box>
                </TabPanel>

                {isEdit && (
                  <TabPanel value="linked" sx={newPhaseItemModalStyles.tabPanel}>
                    <LinkedSection isItemCompleted={isItemCompleted} lagType={activeLagType} />
                  </TabPanel>
                )}
              </Grid>
            </TabContext>
            <Grid sx={newPhaseItemModalStyles.buttonsWrapper}>
              <Grid>
                {isEdit && !!currentPhaseItem && !isItemCompleted && (
                  <ActionButton
                    loading={createLoading || updateLoading}
                    variant="error"
                    label="Delete Item"
                    type="button"
                    onClick={handleOpenPhaseItemDeleteModal}
                  />
                )}
              </Grid>

              <Grid sx={newPhaseItemModalStyles.buttonsContainer}>
                {!isItemCompleted && (
                  <LoadingButton
                    loading={createLoading || updateLoading}
                    variant="outlined"
                    sx={newPhaseItemModalStyles.cancelButton}
                    onClick={handleCloseModal}
                  >
                    Cancel
                  </LoadingButton>
                )}
                {!userPermissions?.MANAGE_SCHEDULE?.canCreate &&
                !userPermissions?.MANAGE_SCHEDULE?.canUpdate ? null : isItemCompleted ? (
                  <ActionButton
                    variant="secondary"
                    label="Mark as Incomplete"
                    type="button"
                    startIcon={<CheckmarkIcon />}
                    onClick={handleSetCompletedPhaseItem}
                    loading={updateLoading}
                  />
                ) : (
                  <LoadingButton
                    color="error"
                    loading={createLoading || updateLoading}
                    variant="contained"
                    type="submit"
                    sx={newPhaseItemModalStyles.saveButton}
                  >
                    Save
                  </LoadingButton>
                )}
              </Grid>
            </Grid>
          </StyledForm>
        </FormProvider>
        {currentPhaseItem && isActivityFeedVisible && project ? (
          <ScheduleActivityFeed scheduleItem={currentPhaseItem} profiles={project.profiles} projectId={activeProjectId} />
        ) : null}
      </Grid>

      {isEdit && !!currentPhaseItem && (
        <DeleteItemModal
          isPhase={false}
          projectId={String(activeProjectId)}
          itemId={deletedPhaseItem?.id}
          closePhaseItemModal={handleClosePhaseItemDeleteModal}
          closeItemModal={handleCloseModal}
          handleDeleteTask={handleDeleteTask}
          fromItemModal={true}
          gantt={gantt}
          ganttItemId={ganttItemId}
          handleAllowUpdateProject={handleAllowUpdateProject}
          handleCloseParentMondal={handleCloseModal}
        />
      )}

      {!!completedPhaseItem && (
        <CompleteItemModal
          updateGanttChart={updateGanttChart}
          open={!!completedPhaseItem}
          itemId={completedPhaseItem?.id}
          itemTitle={completedPhaseItem?.title}
          itemStartDate={completedPhaseItem?.startDate}
          closeModal={handleCloseCompletionModal}
          selectedItem={completedPhaseItem}
          projectId={activeProjectId}
          workingDays={project?.workingDays}
          handleCloseItemModal={handleCloseModal}
          fromEditModal={true}
          gantt={gantt}
          handleSetScrollingId={handleSetScrollingId}
          handleAllowUpdateProject={handleAllowUpdateProject}
        />
      )}

      {!!incompletedPhaseItem && (
        <IncompleteItemModal
          open={!!incompletedPhaseItem}
          item={incompletedPhaseItem}
          projectId={activeProjectId}
          closePhaseItemModal={handleCloseModal}
          closeModal={handleCloseIncompletedModal}
          updateGanttChart={gantt}
          handleAllowUpdateProject={handleAllowUpdateProject}
        />
      )}
    </Dialog>
  );
};

export default PhaseItemModal;
